<template>
  <div class="list trigger-list">
    <div
      v-for="(trigger, index) in triggers"
      :key="trigger.id"
      class="trigger-list__trigger"
    >
      <h3 class="trigger-list__triggertitle">
        {{ trigger.title || $t("objectNames.trigger") + " " + (index + 1) }}
      </h3>

      <div v-if="trigger.chance < 100">
        <!-- This is only shown if the chance is not 100%. -->
        {{ $t("labels.occurrenceProbability") }}{{ $t(":") }}
        {{ trigger.chance }}%
      </div>

      <div v-if="trigger.maxOccurrences != null">
        {{
          $t("texts.triggerMaxOccurrenceCount", {
            maxOccurrences: trigger.maxOccurrences
          })
        }}
      </div>

      <div v-if="!isEmpty(trigger.description)">
        <formatted-text :value="trigger.description" />
      </div>

      <template v-else>
        <div v-if="conditionIds[index].length > 0">
          <h4 class="label mt-0">
            {{ $t("objectNames.condition", conditionIds[index].length) }}
          </h4>
          <ul class="list gap-2 mb-0 mt-1 pl-6">
            <li v-for="conditionId in conditionIds[index]" :key="conditionId">
              <condition-label :condition-id="conditionId" />
            </li>
          </ul>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * A non-editable trigger list
 */
import { computed } from "vue";
import { map } from "lodash-es";
import { isEmpty } from "@/helpers/utility/Text";

import ConditionLabel from "@editor/items/condition/ConditionLabel.vue";
import FormattedText from "@core/ui/FormattedText.vue";

import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";

interface Props {
  incidentId: number;
}
const props = defineProps<Props>();

const triggers = computed(() => {
  const incidentTriggerStore = useIncidentTriggerStore();
  return Object.values(incidentTriggerStore.byIncident[props.incidentId] ?? {});
});

const conditionIds = computed(() => {
  const conditionStore = useConditionStore();
  return map(triggers.value, (trigger) => {
    return conditionStore.getIdsBySource(trigger);
  });
});
</script>

<style lang="scss">
.trigger-list {
  width: max-content;
  max-width: 100%;
  &__trigger {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    border: 1px solid var(--color-border);
  }
  &__triggertitle {
    font-weight: bold;
    color: var(--color-text-medium);
    margin: 0;
    font-size: 1rem;
  }
}
</style>
